// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { enablePostalCodeLocationFinder } from "@bg/App/Store/Selectors/PostalCodeSelector";
import { constants } from "@bg/XDL/constants";
import { getLocalStorage, parseBoolean } from "@bg/XDL/Utilities";
import {
    addToChainAfter,
    HasOnError,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { addLineCollection } from "@insite/client-framework/Services/CartService";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    chain,
    ExecuteOnSuccessCallback,
    PopulateApiParameter,
    SendAddToCartEvent,
    SendDataToApi,
} from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import loadQuote from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuote";

type PropsType = Parameters<typeof PopulateApiParameter>[0];
type ParameterType = PropsType["parameter"];

type XngParameter = ParameterType & {
    newGroup?: boolean;
    groupName: string;
    cartId?: string;
    relatedProduct?: boolean;
    quoteLineId?: string;
    loadCurrentCart?: boolean;
    reloadQuote?: boolean;
};

export const CheckForGroups: typeof PopulateApiParameter = props => {
    const parameter = props.parameter as XngParameter;
    // used in request quote and quote to add a new product to quote
    if (parameter.newGroup) {
        props.apiParameter = {
            ...props.apiParameter,
            cartLineCollection: {
                ...props.apiParameter.cartLineCollection,
                properties: {
                    groupName: parameter.groupName,
                },
            },
            // if on quote page, we have to pass the quote Id
            cartId: parameter.cartId ? parameter.cartId : API_URL_CURRENT_FRAGMENT,
        };
        // used in quote to add related products
    } else if (parameter.relatedProduct) {
        props.apiParameter = {
            ...props.apiParameter,
            cartLineCollection: {
                ...props.apiParameter.cartLineCollection,
                properties: {
                    isQuoteRelatedProducts: "true",
                    quoteLineId: parameter.quoteLineId || "",
                    groupName: parameter.groupName,
                },
            },
            cartId: parameter.cartId ? parameter.cartId : API_URL_CURRENT_FRAGMENT,
        };
    }

    const isAuthenticated = getSession(props.getState()).isAuthenticated;
    const postalCode = getLocalStorage(constants.localStorage.POSTAL_CODE);
    const postalCodeId = getLocalStorage(constants.localStorage.POSTAL_CODE_ID);

    if (enablePostalCodeLocationFinder(props.getState()) && !isAuthenticated && postalCode && postalCodeId) {
        props.apiParameter.cartLineCollection = {
            ...props.apiParameter.cartLineCollection,
            properties: {
                validPostalCodeEntered: "true",
            },
        };
    }
};

export const CustomSendDataToApi: typeof SendDataToApi = async props => {
    try {
        props.apiResult = await addLineCollection(props.apiParameter);
    } catch (error) {
        (props as { error?: string }).error = error.errorMessage;
    }
};

export const XngLoadCurrentCart: typeof ExecuteOnSuccessCallback = props => {
    const parameter = props.parameter as XngParameter;
    if (parameter.loadCurrentCart) {
        props.dispatch(loadCurrentCart());
    }
};

export const ReloadQuoteOnSuccess: typeof SendAddToCartEvent = props => {
    if ((props.parameter as XngParameter).reloadQuote && (props.parameter as XngParameter).cartId) {
        props.dispatch(loadQuote({ quoteId: (props.parameter as XngParameter).cartId! }));
    }
};

export const ExecuteOnErrorCallback: typeof ExecuteOnSuccessCallback = props => {
    const error = (props as { error?: string }).error;
    if (error) {
        markSkipOnCompleteIfOnErrorIsSet(props as { parameter: HasOnError });
        (props.parameter as HasOnError<string>).onError?.(error);
    }
};

export const XngExecuteOnSuccessCallback: typeof ExecuteOnSuccessCallback = props => {
    const error = (props as { error?: string }).error;
    if (!error) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess?.(props.apiResult);
    }
};

addToChainAfter(chain, PopulateApiParameter, CheckForGroups);
replaceInChain(chain, SendDataToApi, CustomSendDataToApi);
addToChainAfter(chain, SendAddToCartEvent, XngLoadCurrentCart);
addToChainAfter(chain, XngLoadCurrentCart, ReloadQuoteOnSuccess);
addToChainAfter(chain, XngLoadCurrentCart, ExecuteOnErrorCallback);
replaceInChain(chain, ExecuteOnSuccessCallback, XngExecuteOnSuccessCallback);
